import algoliasearch from "algoliasearch";
import algoliasearchHelper from "algoliasearch-helper";
import { Dispatch, SetStateAction } from "react";
import { useRouter } from "next/router";
import {
  GENERIC_ERR_MSG,
  GENERIC_EVENT_ERR_HEADING,
} from "@Constants/snackbar";
import {
  apiAddItemToFavorites,
  apiGetFavorites,
  apiRemoveItemFromFavorites,
} from "@wff/api/favoritesApi";
import { cartDrawerActions } from "@wff/store/cartDrawerSlice";
import {
  FavoritesDataSelector,
  addFavorite,
  deleteFavorite,
  resetFavoritesData,
  saveSlugForLastVisitedProduct,
} from "@wff/store/favoritesDataSlice";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import { axiosErrorHandler } from "@wff/utils/snackbarHandlers";
import {
  algoliaToCTTransformer,
  reduceTooltipData,
} from "../components/search/utils/searchUtils";
import { algoliaAPIKey, algoliaAppId, algoliaIndex } from "./algoliaData";
import { useCustomer } from "./useCustomer";

//eslint-disable-next-line sonarjs/cognitive-complexity
export const useFavorites = () => {
  const dispatch = useAppDispatch();
  const { push, query, pathname } = useRouter();
  const productPerBatch = 16;
  const initialSectionLoad = {
    product: 1,
  };

  const pathNameDirectory = pathname.replace("[slug]", "");
  const pathNameSlug =
    typeof query?.slug === "string" ? query?.slug : query?.slug?.[0] ?? "";
  const currentPath = `${pathNameDirectory}${pathNameSlug}`;
  const favoritesData = useAppSelector(FavoritesDataSelector);
  const { favoritesList, slugForLastVisitedProduct } = favoritesData;
  const { isLoggedIn } = useCustomer();
  const { languageCode } = useAppSelector(localeSelector);
  const tooltipData = reduceTooltipData("Title", "Description", "Example");

  const getProductList = async (sku: string) => {
    const algolia = algoliasearch(algoliaAppId, algoliaAPIKey);
    const productsShowLimit = productPerBatch * initialSectionLoad?.product;

    const helper = algoliasearchHelper(algolia, algoliaIndex, {
      facets: ["*"],
      facetFilters: [["sku:" + sku]],
      disjunctiveFacets: [
        "name",
        "category",
        "description",
        "sku",
        "attibutes",
      ],
      hitsPerPage: 500,
      maxValuesPerFacet: 50,
      getRankingInfo: true,
      typoTolerance: true,
    });
    helper.on("result", (algoliaResults) => {
      const productHits = algoliaResults?.results?.hits as any;
      const productList = algoliaToCTTransformer(
        productHits,
        languageCode,
        tooltipData
      ).slice(0, productsShowLimit);
      productList && productList[0] && dispatch(addFavorite(productList[0]));
      return productList[0];
    });
    setTimeout(() => helper.search(), 1000);
  };

  const getFavoritesList = async (listId?: string) => {
    try {
      await apiGetFavorites(listId).then((res: any) => {
        getProducts(res, dispatch, favoritesList, getProductList);
        return true;
      });
    } catch (error: { response: { status: number } } | any) {
      axiosError(error, dispatch);
      return false;
    }
    return false;
  };

  const setFavorite = async (favoriteSKU: string, listId?: string) => {
    const productsToAdd = { sku: [favoriteSKU] };
    try {
      await apiAddItemToFavorites(productsToAdd, listId).then(
        async (res: any) => {
          if (res?.data?.id) {
            await setToFavoriteList(res, favoriteSKU, getProductList);
            return true;
          }
        }
      );
    } catch (error: { response: { status: number } } | any) {
      axiosError(error, dispatch);
      return false;
    }
    return false;
  };

  const removeFavorite = async (favoriteSKU: string, listId?: string) => {
    try {
      await apiRemoveItemFromFavorites(favoriteSKU, listId).then(() => {
        dispatch(deleteFavorite(favoriteSKU));
        return true;
      });
    } catch (error: { response: { status: number } } | any) {
      axiosError(error, dispatch);
      return false;
    }
    return false;
  };

  const saveLastVisitedSlug = () => {
    dispatch(saveSlugForLastVisitedProduct(currentPath ?? ""));
  };

  const clearLastVisitedSlug = () => {
    dispatch(saveSlugForLastVisitedProduct(""));
  };

  const goToLogin = () => {
    push("/account/login");
  };

  const returnToProduct = () => {
    push(slugForLastVisitedProduct);
    clearLastVisitedSlug();
  };

  type TcallbackFn = Dispatch<SetStateAction<boolean>>;
  const handleAddFavorite = async (
    sku: string,
    callbackFn: TcallbackFn,
    isFlyout?: boolean
  ) => {
    isFlyout = isFlyout || false;
    await addFavCallback(
      isLoggedIn,
      setFavorite,
      sku,
      callbackFn,
      saveLastVisitedSlug,
      isFlyout,
      dispatch,
      goToLogin
    );
  };

  const handleRemoveFavorite = async (sku: string, callbackFn: TcallbackFn) => {
    if (isLoggedIn) {
      await isLoggedInRemove();
    } else {
      saveLastVisitedSlug();
      goToLogin();
    }

    async function isLoggedInRemove() {
      const isRemoved = await removeFavorite(sku);
      if (isRemoved) {
        callbackFn(false);
      }
    }
  };

  function axiosError(error: any, dispatch: any) {
    if (error?.response) {
      axiosErrorHandler({
        error,
        dispatch,
        fallbackSnackbar: {
          heading: GENERIC_EVENT_ERR_HEADING.REQUEST_FAILED(),
          message: GENERIC_ERR_MSG.ERROR(),
        },
      });
    }
  }

  async function addFavCallback(
    isLoggedIn: boolean,
    setFavorite: (favoriteSKU: string, listId?: string) => Promise<boolean>,
    sku: string,
    callbackFn: Dispatch<SetStateAction<boolean>>,
    saveLastVisitedSlug: () => void,
    isFlyout: boolean,
    dispatch: any,
    goToLogin: () => void
  ) {
    if (isLoggedIn) {
      await isLoggedInSet();
    } else {
      saveToSlug();
      goToLogin();
    }

    async function isLoggedInSet() {
      const isSet = await setFavorite(sku);
      if (isSet) {
        callbackFn(true);
      }
    }

    function saveToSlug() {
      saveLastVisitedSlug();
      if (isFlyout) {
        dispatch(cartDrawerActions.toggleCartDrawer());
      }
    }
  }

  async function setToFavoriteList(
    res: any,
    favoriteSKU: string,
    getProductList: (sku: string) => Promise<void>
  ) {
    if (!res?.data?.lineItems.some((item: any) => item?.sku === favoriteSKU)) {
      await getProductList(favoriteSKU);
    }
  }

  function getProducts(
    res: any,
    dispatch: any,
    favoritesList: any[],
    getProductList: (sku: string) => Promise<void>
  ) {
    if (res?.data?.id) {
      dispatch(resetFavoritesData());
      mapProducts();
    }

    function mapProducts() {
      res?.data?.lineItems?.map(async (favorite: any) => {
        if (!favoritesList.some((item) => item?.sku === favorite?.sku)) {
          await getProductList(favorite?.sku);
        }
        return true;
      });
    }
  }

  return {
    slugForLastVisitedProduct,
    favoritesList,
    handleAddFavorite,
    handleRemoveFavorite,
    goToLogin,
    returnToProduct,
    getFavoritesList,
    setFavorite,
    removeFavorite,
    saveLastVisitedSlug,
    clearLastVisitedSlug,
  };
};
